import type { InvestmentProjectedListEntry, InvestmentSummary } from "@eu.mdotm.api/sphere";
import type { MaybeAsync } from "@mdotm/utils/promise";
import { sumArrayLike } from "$root/utils";
import {
	ActionText,
	Column,
	Dialog,
	DialogFooter,
	type DialogProps,
	Icon,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { type RwAtom, readAtom, useAtomState, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { type MaybeFn, maybeCall, typedObjectEntries } from "@mdotm/shadcn/legacy/utils";
import { useMemo, type ReactNode } from "react";
import type {
	PortfolioActionUnavailableReason,
	PortfolioBulkActionAvailabilityResult,
} from "./portfolio-actions.const";

export type SpawnBulkAnalysisReviewDialogParams<T> = {
	header: string;
	children: MaybeFn<ReactNode, [RwAtom<T>]>;
	canConfirm: MaybeFn<boolean, [T]>;
	initialState: T;
	onContinue?: MaybeAsync<void, [T]>;
};

export function BulkAnalysisReviewDialog<T>({
	children,
	header,
	show,
	canConfirm,
	onContinue,
	onCancel,
	onAnimationStateChange,
	initialState,
}: Omit<SpawnBulkAnalysisReviewDialogParams<T>, "onContinue"> & {
	onContinue: MaybeAsync<void, [T]>;
	onCancel(): void;
} & Pick<DialogProps, "show" | "onAnimationStateChange">): React.JSX.Element {
	const [state$] = useAtomState(initialState);
	return (
		<Dialog
			size="large"
			show={show}
			onClose={onCancel}
			onAnimationStateChange={onAnimationStateChange}
			header={header}
			footer={
				<DialogFooter
					primaryAction={
						<WithAtom atom$={state$}>
							{(state) => (
								<Button
									disabled={!maybeCall(canConfirm, state)}
									palette="primary"
									size="small"
									data-qualifier="BulkAnalysisReviewDialog/ConfirmButton"
									onClickAsync={() => onContinue(readAtom(state$))}
								>
									Confirm
								</Button>
							)}
						</WithAtom>
					}
					neutralAction={
						<Button
							palette="tertiary"
							size="small"
							type="button"
							onClick={onCancel}
							data-qualifier="BulkAnalysisReviewDialog/CancelButton"
						>
							Cancel
						</Button>
					}
				/>
			}
		>
			{maybeCall(children, state$)}
		</Dialog>
	);
}

type SpawnBulkAnalysisReviewDialogResult<T> = { kind: "continue"; result: T } | { kind: "cancel" } | { kind: "review" };

/** Note: never throws */
export function spawnBulkAnalysisReviewDialog<T>({
	onContinue,
	...forward
}: SpawnBulkAnalysisReviewDialogParams<T>): Promise<SpawnBulkAnalysisReviewDialogResult<T>> {
	return spawn<SpawnBulkAnalysisReviewDialogResult<T>>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<BulkAnalysisReviewDialog
				{...forward}
				show={show}
				onContinue={async (result) => {
					await onContinue?.(result);
					resolve({ kind: "continue", result });
				}}
				onCancel={() => resolve({ kind: "cancel" })}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
			/>
		)),
	).promise;
}

const colorByPalette: Record<"success" | "alert" | "info", string> = {
	success: themeCSSVars.palette_P200,
	alert: themeCSSVars.palette_A200,
	info: themeCSSVars.palette_S200,
};

export function BulkAnalysisReviewSections<T extends InvestmentSummary | InvestmentProjectedListEntry>({
	bulkActionAvailabilityResult,
	midSections,
	getRedirectUrl,
}: {
	bulkActionAvailabilityResult: PortfolioBulkActionAvailabilityResult<T>;
	midSections?: Array<{
		count: number;
		title: string;
		children?: ReactNode;
		palette?: "success" | "alert" | "info";
	}>;
	/** Where "Open this selection in a new tab" leads; without it the link is not offered. */
	getRedirectUrl?(investmentUuid: string[]): string;
}): React.JSX.Element {
	const [available, unavailable] = bulkActionAvailabilityResult;
	const unavailableCount = useMemo(
		() => (!unavailable ? 0 : sumArrayLike(Object.values(unavailable), (x) => x.length)),
		[unavailable],
	);

	const redirectUrl = useMemo(
		() => getRedirectUrl?.(unavailable ? Object.values(unavailable).flatMap((item) => item.map((x) => x.uuid!)) : []),
		[unavailable, getRedirectUrl],
	);
	const sections = useMemo(
		() => [
			...(available.length === 0 && ((midSections?.length ?? 0) > 0 || unavailableCount > 0)
				? []
				: [
						{
							count: available.length,
							title:
								available.length === 1 ? "Portfolio is ready to be processed" : "Portfolios are ready to be processed",
							palette: "success" as const,
						},
					]),
			...(midSections ?? []),
			...(unavailable && unavailableCount > 0
				? [
						{
							count: unavailableCount,
							title: unavailableCount === 1 ? "Portfolio will not be included" : "Portfolios will not be included",
							children: <BulkAnalysisReviewNotSuitableSummary unavailable={unavailable} redirectUrl={redirectUrl} />,
							palette: "alert" as const,
						},
					]
				: []),
		],
		[available.length, midSections, unavailable, unavailableCount, redirectUrl],
	);

	return (
		<ForEach collection={sections}>
			{(section, index) => (
				<Column flexGrow={0}>
					<Row gap={16} flexGrow={1} alignItems="start">
						<Text
							data-qualifier="EligibilityWarning/AvailableEntities"
							as="div"
							className="min-w-[32px] py-1 px-2 text-center rounded-lg"
							type="Body/L/Bold"
							style={{
								backgroundColor: colorByPalette[section.palette ?? ("info" as const)],
							}}
						>
							{section.count}
						</Text>
						<Column flexGrow={1}>
							<Text as="div" type="Body/L/Medium" className="mt-1">
								{section.title}
							</Text>
							{section.children && (
								<Column flexGrow={0} className="my-4">
									{section.children}
								</Column>
							)}
						</Column>
					</Row>
					{index < sections.length - 1 && (
						<div className="border-b w-10 my-4" style={{ borderBottomColor: themeCSSVars.palette_N100 }} />
					)}
				</Column>
			)}
		</ForEach>
	);
}

export function BulkAnalysisReviewNotSuitableSummary<T extends InvestmentSummary | InvestmentProjectedListEntry>({
	unavailable,
	redirectUrl,
}: {
	unavailable: Record<PortfolioActionUnavailableReason, T[]>;
	redirectUrl: string | undefined;
}): React.JSX.Element {
	return (
		<Column flexGrow={0} gap={8}>
			<ul className="list-disc pl-4">
				<ForEach
					collection={typedObjectEntries(unavailable).filter(([_reason, investments]) => investments.length > 0)}
				>
					{([reason, investments]) => (
						<li>
							<Text type="Body/M/Book">{PortfolioActionUnavailableReasonToMessageMap[reason](investments.length)}</Text>
						</li>
					)}
				</ForEach>
			</ul>
			{redirectUrl != null && (
				<Row gap={4} alignItems="center" flexGrow={0}>
					<ActionText href={redirectUrl} target="_blank" rel="opener">
						Open this selection in a new tab
					</ActionText>
					<Icon icon="Expand" />
				</Row>
			)}
		</Column>
	);
}

export const PortfolioActionUnavailableReasonToMessageMap: Record<
	PortfolioActionUnavailableReason,
	(count: number) => string
> = {
	ERROR: (count) =>
		count === 1
			? `${count} portfolio is in ERROR status and cannot proceed with the selected action.`
			: `${count} portfolios are in ERROR status and cannot proceed with the selected action.`,
	CALCULATING: (count) =>
		count === 1
			? `${count} portfolio is in CALCULATING status and must complete processing before any action can be performed.`
			: `${count} portfolios are in CALCULATING status and must complete processing before any action can be performed.`,
	REVIEW: (count) =>
		count === 1
			? `${count} portfolio is in REVIEW status and must be resolved before proceeding.`
			: `${count} portfolios are in REVIEW status and must be resolved before proceeding.`,
	RETRIEVING_DATA: (count) =>
		count === 1
			? `${count} portfolio is in RETRIEVING DATA status and cannot be modified until data retrieval is completed.`
			: `${count} portfolios are in RETRIEVING DATA status and cannot be modified until data retrieval is completed.`,
	DRAFT: (count) =>
		count === 1
			? `${count} portfolio is in DRAFT status and must be finalized before performing this action.`
			: `${count} portfolios are in DRAFT status and must be finalized before performing this action.`,
	PROPOSAL_READY: (count) =>
		count === 1
			? `${count} portfolio is in PROPOSAL READY status and require review before proceeding.`
			: `${count} portfolios are in PROPOSAL READY status and require review before proceeding.`,
	ACCEPTED: (count) =>
		count === 1
			? `${count} portfolio is in ACCEPTED and doesn't have an associated proposal.`
			: `${count} portfolios are in ACCEPTED and don't have associated proposals.`,
	READY: (count) =>
		count === 1
			? `${count} portfolio is in READY status and doesn't have an associated proposal.`
			: `${count} portfolios are in READY status and don't have associated proposals.`,
	CANNOT_BULK_ENHANCE: (count) =>
		count === 1 ? `${count} portfolio has unfeasible constraints.` : `${count} portfolios have unfeasible constraints.`,
	UNIVERSE_NOT_READY: (count) =>
		count === 1
			? `${count} portfolio has a universe which is not in READY status.`
			: `${count} portfolios have universes which are not in READY status.`,
	MISSING_UNIVERSE: (count) =>
		count === 1
			? `${count} portfolio has no associated universe and cannot proceed without one.`
			: `${count} portfolios have no associated universe and cannot proceed without one.`,
	CANNOT_MIX: (count) =>
		count === 1
			? `${count} portfolio has another portfolio in its composition and cannot proceed due to nesting restrictions.`
			: `${count} portfolios have another portfolio in their composition and cannot proceed due to nesting restrictions.`,
	TOO_OLD: (count) =>
		count === 1
			? `${count} portfolio has an outdated proposal that must be refreshed before proceeding.`
			: `${count} portfolios have an outdated proposal that must be refreshed before proceeding.`,
	ACL_EDITOR_REQUIRED: (count) =>
		count === 1
			? `${count} portfolio is excluded because the user does not have the necessary permissions to perform this action.`
			: `${count} portfolios are excluded because the user does not have the necessary permissions to perform this action.`,
	ACL_OWNER_REQUIRED: () => "You are not the owner",
	USED_AS_NESTED: (count) =>
		count === 1
			? `${count} portfolio is included in another entity’s composition and cannot be deleted independently.`
			: `${count} portfolios are included in another entity’s composition and cannot be deleted independently.`,
	EXECUTING: (count) =>
		count === 1
			? `${count} portfolio is in EXECUTING status and must complete processing before any action can be performed.`
			: `${count} portfolios are in EXECUTING status and must complete processing before any action can be performed.`,

	NOT_EXECUTING: (count) =>
		count === 1
			? `${count} portfolio is not being executed and cannot be stopped.`
			: `${count} portfolios are not being executed and cannot be stopped.`,

	PAPER_TRADING: (count) =>
		count === 1
			? `${count} portfolio is a paper trading portfolio and cannot be sent to execution.`
			: `${count} portfolios are paper trading portfolios and cannot be sent to execution.`,
	EXECUTION_ONLY: (count) =>
		count === 1
			? `${count} portfolio cannot be accepted because it can only be sent to execution.`
			: `${count} portfolios cannot be accepted because they can only be sent to execution.`,
	NOT_YET_INCEPTED: (count) =>
		count === 1
			? `${count} portfolio did not exist on the selected date.`
			: `${count} portfolios did not exist on the selected date.`,
};
